import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { evProductNotifyMe, evSelectSizeButton } from 'events/favorites';

import { PRODUCT_ASIN } from 'common/regex';
import { fetchProductDetail } from 'actions/productDetail';
import { fireProductNotifyMe } from 'actions/account/account';
import { addOosItem } from 'actions/hearts';
import { trackEvent } from 'helpers/analytics';
import { getAsinFromProductUrl } from 'helpers/MyAccountUtils';
import { track } from 'apis/amethyst';
import css from 'styles/components/account/favoriteItemActions.scss';

const initialState = {
  dimensions: null,
  colorStock: null,
  selected: {}
};

export class FavoriteItemActions extends Component {
  static contextTypes = {
    marketplace: PropTypes.object,
    testId: PropTypes.func
  };

  static propTypes = {
    item: PropTypes.object.isRequired,
    onAddToCart: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired
  };

  state = initialState;

  componentDidMount() {
    const { addOosItem, item } = this.props;
    const { asin, quantity } = item;
    const { stockId } = this.state;
    if (!(asin || stockId || quantity)) {
      addOosItem(item);
    }
  }

  // responsible for filtering down the selected dimensions from the stockData
  onDimensionChange = ({ target }) => {
    const { name, value } = target;
    const { selected } = this.state;
    if (value) {
      this.setState({
        selected: {
          ...selected,
          [name]: value
        }
      }, this.onDimensionChangeCallback);
    } else {
      const newSelected = { ...this.state.selected };
      delete newSelected[name];

      this.setState({
        selected: { ...newSelected }
      }, this.onDimensionChangeCallback);
    }

  }

  onDimensionChangeCallback = () => {
    const { selected, dimensions } = this.state;
    const sKeys = Object.keys(selected);
    const isDimsSelected = sKeys.length === dimensions.length;

    let matchedDim = null;
    if (isDimsSelected) {
      const { colorStock } = this.state;
      const d1 = selected[sKeys[0]];
      const d2 = selected[sKeys[1]];

      colorStock.forEach(dim => {
        const dv = Object.values(dim);
        // if two dimensions exist, make sure to check both
        if (d2) {
          if (dv.includes(d1) && dv.includes(d2)) {
            matchedDim = dim;
          }
        } else {
          if (dv.includes(d1)) {
            matchedDim = dim;
          }
        }
      });
    }

    this.checkStock(matchedDim, isDimsSelected);
  }

  // when an item is out of stock, fire the email notification API
  onNotifyMe = (id, e) => {
    e.preventDefault();
    const { item, fireProductNotifyMe } = this.props;
    const IDTYPE = PRODUCT_ASIN.test(id) ? 'asins' : 'stockIds';

    if (id) {
      this.setState(initialState);
      fireProductNotifyMe({
        [IDTYPE]: [id]
      }).then(() => {
        track(evProductNotifyMe, item);
        trackEvent('TE_FAVORITES_OOSNOTIFY', id);
      });
    }
  }

  // when you click the 'Select Size' button, make a PDP call to gather product data
  onRenderDimensions = item => {
    const { productUrl } = item;
    const { fetchProductDetail } = this.props;
    const asin = getAsinFromProductUrl(productUrl);
    const itemWithAsin = {
      ...item,
      asin
    };

    track(evSelectSizeButton, itemWithAsin); // send amethyst select size button tracking data

    fetchProductDetail({ asin, includeOosSizing: true })
      .then(() => {
        const {
          product: {
            colorId,
            detail: { sizing }
          }
        } = this.props;
        const { dimensions, stockData } = sizing;

        // filter specific color from stockData
        const colorStock = stockData.filter(({ color }) => color === colorId);

        // this product has no dimensions
        if (dimensions.length === 0) {
          const { onHand, id } = colorStock[0];
          const oos = !!(onHand <= 0);

          this.setState({
            isDimsSelected: true,
            stockId: id,
            oos
          });
        }

        this.setState({
          dimensions,
          colorStock
        });
      });
  }

  // sets stockId local state and oos param
  checkStock = (dim, isDimsSelected) => {
    // if you havent selected all dimensions, clear stockId from state
    const newState = {
      ...this.state,
      isDimsSelected,
      oos: undefined,
      stockId: undefined
    };

    if (!isDimsSelected) {
      this.setState({
        ...newState
      });
    } else if (!dim) {
      this.setState({
        ...newState,
        oos: true
      });
    } else {
      const { id, onHand } = dim;
      const oos = !!(onHand <= 0);

      this.setState({
        ...newState,
        stockId: id,
        oos
      });
    }
  }

  // make a product bundle call, get stockId back and add it to cart
  fetchProductAndAdd = ({ productUrl }) => {
    const { fetchProductDetail, onAddToCart } = this.props;

    fetchProductDetail({ asin: getAsinFromProductUrl(productUrl), includeOosSizing: true })
      .then(() => {
        const {
          product: {
            colorId,
            detail: { sizing }
          }
        } = this.props;

        const { stockData } = sizing;

        // filter specific color from stockData
        const colorStock = stockData.find(({ color }) => color === colorId);
        colorStock && onAddToCart({ asin: colorStock.id });
      });
  }

  makeCTAButton = () => {
    const {
      item,
      onAddToCart,
      account: { oosMessages = [] }
    } = this.props;

    const {
      marketplace: {
        features: { showOOSNotifyMe },
        cart: { cartName }
      }
    } = this.context;

    const { testId } = this.context;

    const {
      oneSize,
      quantity,
      asin
    } = item;

    const { dimensions, isDimsSelected, stockId, oos } = this.state;

    const id = asin || stockId;

    const isNotified = oosMessages.includes(id);

    // if product is not in stock
    if (!quantity || oos) {
      // make sure marketplace allows for OOS notification, and also that the stockId is still in the catalog
      if (showOOSNotifyMe && id) {
        return (
          isNotified
            ? <div className={css.notified}>
              <span data-test-id={testId('notifyMeText')}>You will be notified when this item comes back in stock.</span>
            </div>
            : <button
              type="button"
              className={css.secondaryCTA}
              onClick={this.onNotifyMe.bind(this, id)}
              data-test-id={testId('notifyMeButton')}>
                Notify Me
            </button>
        );
      }

      // return "Out of Stock" for non-oos-notification marketplaces, or products not in the catalog
      return (
        <button
          type="button"
          disabled={true}
          className={css.disabledCTA}
          data-test-id={testId('outOfStock')}>
            Out of stock
        </button>
      );
    }

    // there are no dimensions to choose from, but we still need to make a call to productBundle to get stockId
    if (oneSize) {
      return (
        <button
          type="button"
          className={css.primaryCTA}
          onClick={this.fetchProductAndAdd.bind(this, item)}
          data-test-id={testId('addToCartNoDimension')}>
        Add To {cartName}
        </button>
      );
    }

    // id means the selected dimension or favorite has an id, and all dimensions have been selected
    if (id || dimensions) {
      const itemWithAsin = {
        ...item,
        asin,
        stockId
      };
      return (
        <button
          type="button"
          disabled={!asin && !isDimsSelected}
          className={css.primaryCTA}
          onClick={onAddToCart.bind(this, itemWithAsin)}
          data-test-id={testId('addToCart')}>
        Add To {cartName}
        </button>
      );
    }

    // if there are no dimensions, show the Select Size button, otherwise, render nothing (no CTA until selections are made)
    if (!dimensions) {
      return (
        <button
          type="button"
          className={css.secondaryCTA}
          data-test-id={testId('selectSize')}
          onClick={this.onRenderDimensions.bind(this, item)}>
          Select Size
        </button>
      );
    }
  };

  makeDimensionDropdown = ({ name, units }) => {
    const { values } = units[0];
    const { testId } = this.context;

    return (
      <select
        name={name}
        onChange={this.onDimensionChange.bind(this)}
        required
        data-test-id={testId(name + 'Dropdown')}>
        <option value="">Select {name}</option>
        {values.map(({ id, value }) =>
          <option key={id} value={id}>{value}</option>
        )}
      </select>
    );
  }

  makeDimensions = () => {
    const {
      dimensions
    } = this.state;

    // product is a favorite, or product has no dimensions (jewelry, sunglasses, etc)
    if (!(dimensions && dimensions.length)) {
      return null;
    }

    return (
      <form method="post" className={css.dimensions}>
        {dimensions.map(dim => {
          const { name } = dim;

          return (
            <Fragment key={name}>
              <label htmlFor={name}>{name}</label>
              {this.makeDimensionDropdown(dim)}
            </Fragment>
          );
        })}
      </form>
    );
  }

  render() {
    const { item, onRemove } = this.props;
    const { testId } = this.context;
    const { productUrl, asin } = item;
    const itemWithAsin = {
      ...item,
      asin: getAsinFromProductUrl(productUrl)
    };

    return (
      <div>
        { !asin && this.makeDimensions() }
        {this.makeCTAButton()}

        <button
          type="button"
          className={css.remove}
          onClick={onRemove.bind(this, itemWithAsin)}
          data-test-id={testId('remove')}>
          Remove
        </button>
      </div>
    );
  }
}

export function mapStateToProps(state) {
  return {
    account: state.account,
    product: state.product
  };
}

export default connect(mapStateToProps, {
  addOosItem,
  fetchProductDetail,
  fireProductNotifyMe
})(FavoriteItemActions);
